import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import CryptoJS from "crypto-js";
import { minBy } from "lodash";

import {
	IAuthReducerState,
	IChangeDefaultPasswordRequestData,
	IGetFrameImageResponseData,
	ILoginUserRequestData,
	ILoginUserResponseData,
	IVerifySignupTokenResponseData
} from "../../types";
import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import authServices from "../services/auth.services";
import { queries } from "./queries.reducer";

const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY ? String(process.env.REACT_APP_ENCRYPTION_KEY) : "";

const initialState: IAuthReducerState = {
	loading: false,
	success: false,
	error: "",
	currentAction: "",
	unauthorizedError: false,
	defaultPasswordError: false,
	resetPasswordError: false,
	user: {
		token: "",
		full_name: "",
		role: 0,
		group_id: 0,
		group_name: "",
		customer: null,
		username: "",
		email: "",
		enable_ai_search: false,
		use_group_permissions_only: true,
		permissions: null
	},
	cachedImagesMap: {},
	otpVerified: false
};

/**
 * LOGIN USER
 */
export const loginUser = createAsyncThunk<ILoginUserResponseData, ILoginUserRequestData>(
	"auth/loginUser",
	authServices.handleLoginUser
);

// LOGOUT USER
export const logoutUser = createAsyncThunk<void>("auth/logoutUser", authServices.handleLogoutUser);

const authReducer = createSlice({
	name: "auth",
	initialState: initialState,
	reducers: {
		clearUnauthState: (state) => {
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.user.token = "";
		},

		clearAuthState: (state) => {
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.user = {
				token: "",
				full_name: "",
				role: 0,
				group_id: 0,
				group_name: "",
				customer: null,
				username: "",
				email: "",
				enable_ai_search: false,
				use_group_permissions_only: true,
				permissions: null
			};
		},

		updateCachedImageTimestamp: (state, { payload }: PayloadAction<{ key: string; timestamp: number }>) => {
			const { key, timestamp } = payload;
			state.cachedImagesMap[key].timestamp = timestamp;
		},

		addCachedImageData: (
			state,
			{ payload }: PayloadAction<{ key: string; data: IGetFrameImageResponseData; timestamp: number }>
		) => {
			const { key, data, timestamp } = payload;

			const updatedCachedImagesMap = { ...state.cachedImagesMap };
			updatedCachedImagesMap[key] = { data, timestamp };

			const plateImages = Object.entries(updatedCachedImagesMap)
				.filter(([key]) => key.startsWith("plate"))
				.map(([key, value]) => ({
					...value,
					key
				}));

			if (plateImages.length > 300) {
				const oldestPlateImage = minBy(plateImages, "timestamp");
				if (oldestPlateImage) delete updatedCachedImagesMap[oldestPlateImage.key];
			}

			const frameImages = Object.entries(updatedCachedImagesMap)
				.filter(([key]) => key.startsWith("frame"))
				.map(([key, value]) => ({
					...value,
					key
				}));

			if (frameImages.length > 100) {
				const oldestFrameImage = minBy(frameImages, "timestamp");
				if (oldestFrameImage) delete updatedCachedImagesMap[oldestFrameImage.key];
			}

			state.cachedImagesMap = updatedCachedImagesMap;
		},

		updateToken: (state, { payload }: PayloadAction<string>) => {
			state.user.token = CryptoJS.AES.encrypt(payload, encryptionKey).toString();
		},

		verifyOTP: (state) => {
			state.otpVerified = true;
		}
	},
	extraReducers: (builder) => {
		// LOGIN USER
		builder.addCase(loginUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.otpVerified = false;
		});

		builder.addCase(loginUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.user = {
				token: "",
				full_name: "",
				role: 0,
				group_id: 0,
				group_name: "",
				customer: null,
				username: "",
				email: "",
				enable_ai_search: false,
				use_group_permissions_only: true,
				permissions: null
			};
			state.otpVerified = false;
		});

		builder.addCase(loginUser.fulfilled, (state, { payload }) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.user = {
				...payload,
				token: CryptoJS.AES.encrypt(payload.token, encryptionKey).toString(),
				permissions: payload.use_group_permissions_only ? payload.group_permissions : payload.user_permissions
			};
			state.otpVerified = false;
		});

		// LOGOUT USER
		builder.addCase(logoutUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
		});

		builder.addCase(logoutUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
		});

		builder.addCase(logoutUser.fulfilled, (state) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
			state.defaultPasswordError = false;
			state.resetPasswordError = false;
			state.user = {
				token: "",
				full_name: "",
				role: 0,
				group_id: 0,
				group_name: "",
				customer: null,
				username: "",
				email: "",
				enable_ai_search: false,
				use_group_permissions_only: true,
				permissions: null
			};
			state.otpVerified = false;
			state.cachedImagesMap = {};
		});
	}
});

const authApi = queries.injectEndpoints({
	endpoints: (builder) => ({
		// REQUEST OTP
		requestOTP: builder.query<void, void | null>({
			query: () => ({
				url: "/auth/rbac/users/request_login_otp/",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// VERIFY LOGIN OTP
		verifyLoginOTP: builder.mutation<void, string>({
			query: (otp) => ({
				url: "/auth/rbac/users/verify_login_otp/",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: { otp }
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// VERIFY SIGNUP TOKEN
		verifySignupToken: builder.query<IVerifySignupTokenResponseData, string>({
			query: (token) => ({
				url: "/auth/rbac/users/verify_signup_otp/",
				headers: getAPIHeaderOptions(),
				method: "POST",
				body: { token }
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// CHANGE DEFAULT PASSWORD
		changeDefaultPassword: builder.mutation<void, IChangeDefaultPasswordRequestData>({
			query: (requestData) => ({
				url: "/auth/rbac/users/change_default_password/",
				headers: { ...getAPIHeaderOptions(), Authorization: `Token ${requestData.token}` },
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		})
	})
});

export const {
	clearAuthState,
	clearUnauthState,
	addCachedImageData,
	updateCachedImageTimestamp,
	updateToken,
	verifyOTP
} = authReducer.actions;

export const {
	// REQUEST OTP
	useRequestOTPQuery,
	// VERIFY LOGIN OTP
	useVerifyLoginOTPMutation,
	// VERIFY SIGNUP TOKEN
	useVerifySignupTokenQuery,
	// CHANGE DEFAULT PASSWORD
	useChangeDefaultPasswordMutation
} = authApi;

export default authReducer;
